/**
 * Domain snapshot → React Query cache mirror.
 *
 * Called from:
 *   1. bootstrap.ts (one-shot on cold load, after /api/state/bootstrap)
 *   2. stateChannel.ts (every WS state_init / state_delta)
 *
 * **Conservative re-enable (2026-04-26)** — only sub-field-to-array
 * bindings where the snapshot field shape matches the legacy
 * `fetchXXX()` return type AND we can prove it with `Array.isArray`.
 *
 * Wrapper-to-object bindings (e.g. writing the entire system_health
 * snapshot to queryKeys.systemHealth) are intentionally still disabled.
 * Bootstrap snapshots carry `_seq` / `as_of` / `mode` fields that the
 * legacy fetchers don't return, and verifying object-shape compatibility
 * key-by-key is too risky for a same-day fix. Sub-field array bindings
 * are safer because the type contract is just "is it an array?".
 *
 * The 2026-04-26 incident root cause was writing the WHOLE wrapper to
 * a queryKey whose consumers expected a plain array — `holdings.map()`
 * and `orders.filter()` then crashed. The Array.isArray guard prevents
 * that exact bug.
 *
 * To add a new binding: verify the legacy fetcher's return type in
 * `src/api/portfolioApi.ts` etc., add a guarded set() call below, and
 * pin it with a test in __tests__/queryCacheMirror.test.ts.
 */
import { queryClient } from "@/api/queryClient";
import { queryKeys } from "@/api/queryKeys";
import type { DomainKey } from "@/state/store";

type Snap = Record<string, unknown> | null | undefined;

/** Write to RQ cache only when `data` is an array (defensive). */
function setIfArray(key: readonly unknown[], data: unknown): void {
  if (!Array.isArray(data)) return;
  queryClient.setQueryData(key as readonly unknown[], data);
}

/** Write a wrapper object to RQ cache. Used for snapshots that are
 *  superset-shape-compatible with the legacy fetcher's return type. */
function setIfObject(key: readonly unknown[], data: unknown): void {
  if (!data || typeof data !== "object" || Array.isArray(data)) return;
  queryClient.setQueryData(key as readonly unknown[], data);
}

export function mirrorDomainToQueryCache(domain: DomainKey, snapshot: Snap): void {
  if (!snapshot) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = snapshot as any;

  switch (domain) {
    case "orders":
      // 2026-04-27: I8 fix.
      // queryKeys.orders → fetchOrders returns Order[] (plain array). OK.
      // queryKeys.paperOrders → fetchPaperOrders returns {orders: [...], ...wrapper}.
      // Wrap the array so consumers calling .orders.map() don't crash.
      setIfArray(queryKeys.orders, d.today);
      if (Array.isArray(d.today)) {
        queryClient.setQueryData(queryKeys.paperOrders, { orders: d.today });
      }
      // 2026-04-27 Phase B: ordersByScope("today") consumed by 5 index pages
      // (Nifty/FinNifty/MidcpNifty/Equity/StockFO) for client-side filter.
      // Same data — write to that cache key too so the polls can be gated.
      setIfArray(queryKeys.ordersByScope("today"), d.today);
      break;

    case "positions":
      // 2026-04-27: I8 fix. fetchPaperPositions returns
      // {positions, closed_positions, total_unrealised_pnl} wrapper.
      // Mirror BOTH open + closed_today now that the aggregator snapshot
      // includes closed_today (was open-only — closed trades invisible
      // in dashboard via state-aggregator path).
      if (Array.isArray(d.open)) {
        queryClient.setQueryData(queryKeys.paperPositions, {
          positions:        d.open,
          closed_positions: Array.isArray(d.closed_today) ? d.closed_today : [],
          total_unrealised_pnl: d.totals?.total_unrealised_pnl ?? 0,
        });
      }
      // 2026-04-27 Phase D: also mirror closed_today array to the
      // closedPositions queryKey used by Positions.tsx + TradeQueue.
      // fetchClosedPositions returns ClosedPosition[] (bare array).
      setIfArray(queryKeys.closedPositions, d.closed_today);
      break;

    case "signals":
      // 2026-04-27 Phase D: signals.pending mirrored to pendingSignals.
      // fetchPendingSignals returns Signal[] (bare array).
      setIfArray(queryKeys.pendingSignals, d.pending);
      break;

    case "gtt":
      // 2026-04-27: I8 fix. /gtt/active returns {count, gtts: [...]} not bare
      // array. GTTPage destructures `data.gtts` — must wrap.
      if (Array.isArray(d.active)) {
        queryClient.setQueryData(queryKeys.gttActive, {
          count: d.count ?? d.active.length,
          gtts:  d.active,
        });
      }
      break;

    case "scheduler_status":
      // SchedulerPage consumes an array of jobs; snap.jobs is the array.
      // fetchSchedulerJobs returns SchedulerJob[] — array shape OK.
      setIfArray(queryKeys.schedulerJobs, d.jobs);
      break;

    case "feature_flags":
      // 2026-04-27: I8 fix. fetchFeatureFlags returns {flags: [...]} wrapper,
      // not bare array. FeatureFlagsPanel reads `data.flags` — must wrap.
      if (Array.isArray(d.flags)) {
        queryClient.setQueryData(queryKeys.featureFlags, { flags: d.flags });
      }
      break;

    case "system_health":
      // Verified 2026-04-26: snapshot is a superset of /api/health/system
      // legacy response (extra _seq + as_of fields ignored by consumers).
      setIfObject(queryKeys.systemHealth, d);
      // token_expiry sub-field is present on both — mirror so the
      // separate `tokenExpiry` queryKey consumers stay fresh too.
      setIfObject(queryKeys.tokenExpiry,  d.token_expiry);
      break;

    case "metrics":
      // Verified 2026-04-26: snapshot is a superset of /api/metrics/summary
      // (extra _seq + migration fields are tolerated by MetricsDashboard).
      setIfObject(queryKeys.metricsSummary, d);
      // 2026-04-27: I8 fix. Legacy /api/metrics/anomalies returns
      // {anomalies: [...], anomaly_count, status}. snap.anomalies is just
      // the array. Wrap so MetricsDashboard's destructuring works.
      if (Array.isArray(d.anomalies)) {
        queryClient.setQueryData(queryKeys.metricsAnomalies, {
          anomalies:     d.anomalies,
          anomaly_count: d.anomalies.length,
          status:        d.anomalies.length > 0 ? "anomalies_detected" : "ok",
        });
      } else if (d.anomalies && typeof d.anomalies === "object") {
        // Already a wrapper object — pass through.
        queryClient.setQueryData(queryKeys.metricsAnomalies, d.anomalies);
      }
      break;

    case "indices":
      // Verified 2026-04-26: snapshot has same top-level keys as
      // /api/banknifty/indices/status EXCEPT `stock_futures` which is
      // sourced separately from the scan_monitor WS message (consumers
      // already use `?? []` defensive guards).
      setIfObject(queryKeys.indicesStatus, d);
      break;

    case "holdings":
      // 2026-04-27: legacy /api/portfolio/holdings returns a bare KiteHolding[].
      // Snapshot has it at d.holdings — mirror as-is so useQuery({holdings})
      // finds primed cache + skips its REST fetch.
      setIfArray(queryKeys.holdings, d.holdings);
      break;

    case "charges":
      // 2026-04-27: legacy /api/portfolio/charges?trade_date=today returns
      // {summary, breakdown, trades, mode}. Snapshot has summary + breakdown
      // (no per-symbol trades — that's served by the deep-dive endpoint).
      // Mirror as-is so the dashboard "Charges" stat card finds primed cache.
      setIfObject(queryKeys.chargesReport("today"), d);
      break;

    case "system_toggles":
      // 2026-04-27 Phase H: mirror 4 sub-fields to their legacy queryKeys so
      // the existing useQuery consumers find primed data + skip their REST
      // polls. Field shapes match the legacy endpoint responses verified in
      // backend/app/services/state_aggregator/system_toggles.py builders.
      setIfObject(queryKeys.emergencyStopStatus,   d.emergency_stop);
      setIfObject(queryKeys.autoTradeStatus,       d.auto_trade);
      setIfObject(queryKeys.autoBankniftyStatus,   d.bn_auto);
      setIfObject(queryKeys.crashProtectionStatus, d.crash_protection);
      break;

    case "system_extras":
      // 2026-04-28 (audit fallout): pnl_by_index aggregator shape is
      // {breakdown: {INDEX: {...}}, as_of} but legacy REST endpoint
      // /portfolio/pnl-by-index returns {date, total_pnl, by_index}.
      // Mirroring crashed IndexPnlBreakdown.tsx (reads data.by_index).
      // Disabled this mirror until aggregator is reshaped to match the
      // legacy contract OR consumer is rewritten to handle both shapes.
      // Other 2 sub-fields (lifecycles, bn_directional) shape-match.
      // setIfObject(queryKeys.pnlByIndex,         d.pnl_by_index);
      setIfObject(queryKeys.positionLifecycles, d.position_lifecycles);
      setIfObject(queryKeys.bnDirectionalState, d.bn_directional);
      break;

    // ── Intentionally NOT mirrored (incompatible shapes):
    //
    //   portfolio    → /api/portfolio/ returns a flat object
    //                  {cash_balance, total_invested, ...}; snapshot is
    //                  nested {cash, pnl, exposure, drawdown}.
    //                  Consumers expecting flat keys would crash.
    //   auth_status  → /api/auth/status returns {authenticated} only;
    //                  snapshot is {kite_authenticated, kite_token, ...}.
    //                  Different field names.
    //
    // To re-enable, write an adapter in this case branch that maps the
    // snapshot fields to the legacy shape before calling setQueryData.
    case "portfolio":
    case "auth_status":
    case "trades":
    case "alerts":
    case "settings_meta":
    case "dispatcher":
      break;
  }
}

// 2026-04-27 polling clean-sweep — dev-mode regression guard.
//
// If a useQuery fires for a queryKey that has a corresponding state-aggregator
// domain (i.e. data is also being pushed via WS), warn in console so future
// PRs that forget to gate the poll on `isStateStoreEnabled()` get caught
// during development. No-op in production (process.env.NODE_ENV check).
//
// Wired by the queryClient onSuccess defaultOption in api/queryClient.ts.
const _MIRRORED_KEY_PREFIXES: readonly string[] = [
  "holdings",          // Phase 2
  "chargesReport",     // Phase 3
  "ordersByScope",     // Phase B (today)
  "indicesStatus",     // Phase B
  "closedPositions",   // Phase C / Phase D
  "pendingSignals",    // Phase D
  "orders",            // multiple phases
  "portfolio",         // earlier (StoreSync)
  "paperPositions",    // earlier (StoreSync via positions domain)
];

export function isMirroredQueryKey(key: readonly unknown[]): boolean {
  const head = String(key[0] ?? "");
  return _MIRRORED_KEY_PREFIXES.includes(head);
}

